import { useMutation, useQueryClient } from '@tanstack/react-query'

import { apiErrorMessage } from '@/lib/api-error-message'
import { policyCatalogKeys, policyOrderKeys } from '@/modules/hierarchy'
import { preferredCounterPolicyApi, type PreferredCounterPolicy, type PreferredCounterEdit } from '../../api/preferred-counter-policy-api'
import { printerPolicyDefinitionKeys } from '../printer-policies/printer-policy-definition-query'

export function useSavePreferredCounterPolicy(entityId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ policy, edit, operationId }: { policy: PreferredCounterPolicy | null; edit: PreferredCounterEdit; operationId: string }) => policy
      ? preferredCounterPolicyApi.update(entityId, policy, edit)
      : preferredCounterPolicyApi.create(entityId, edit, operationId),
    onSuccess: () => Promise.all([
      client.invalidateQueries({ queryKey: policyCatalogKeys.all(entityId) }),
      client.invalidateQueries({ queryKey: policyOrderKeys.all(entityId) }),
      client.invalidateQueries({ queryKey: printerPolicyDefinitionKeys.all(entityId) }),
    ]),
  })
}

export function useDeletePreferredCounterPolicy(entityId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (policy: Pick<PreferredCounterPolicy, 'id' | 'revision'>) => preferredCounterPolicyApi.delete(entityId, policy),
    onSuccess: () => Promise.all([
      client.invalidateQueries({ queryKey: policyCatalogKeys.all(entityId) }),
      client.invalidateQueries({ queryKey: policyOrderKeys.all(entityId) }),
      client.invalidateQueries({ queryKey: printerPolicyDefinitionKeys.all(entityId) }),
    ]),
  })
}

export function preferredCounterPolicyError(error: unknown, operation: 'save' | 'load' | 'delete' = 'save') {
  const fallback = {
    save: 'Couldn’t save the preferred counters policy. Your edits are kept. Try again.',
    load: 'Couldn’t load the preferred counters policy. Your edits are kept. Try reloading again.',
    delete: 'Couldn’t delete the preferred counters policy. Cancel and refresh to check whether it already succeeded.',
  }[operation]
  return apiErrorMessage(error, fallback, {
    codes: {
      POLICY_ORDER_CONFLICT: 'The policy order changed. Reload the order, review the position, and save again.',
      PRINTER_POLICY_CONFLICT: 'This policy changed or the request conflicts with an earlier operation. Reload the latest policy before saving again. Reloading replaces your unsaved edits.',
      INVALID_PRINTER_POLICY: 'Check the policy values and selector before saving again.',
      PRINTER_POLICY_EVALUATOR_UNAVAILABLE: 'Policy validation is unavailable. Try again shortly.',
      PRINTER_POLICY_NOT_FOUND: 'This policy no longer exists. Close the editor and refresh the list.',
    },
  })
}
